import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  AdminApiTags,
  reducerPath as reducerPathAdmin,
} from "../../api/network/adminApiServices";
import { reducerPath as reducerPathCamera } from "../../api/network/camerasAdminVMApiService";
import {
  CamerasAdminVMApiTags,
  useDashNotifyQuery,
} from "../../api/network/vmApiService";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { VMDashNotifyResponseIncomingCall } from "../../api/types/CamerasVisionMax";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../pages/admin-details/role/constants";
import { CAMERA_TYPES } from "../../pages/admin-details/vehicle/constants";
import ModalVMCameraLiveview from "../../pages/camera-admin/management/modalVMCameraLiveview";
import ModalVMCameraLiveviewContinue from "../../pages/camera-admin/management/modalVMCameraLiveviewContinue";
import ModalVMCameraVoicecall from "../../pages/camera-admin/management/modalVMCameraVoicecall";
import ModalVMCameraVoicecallIncoming from "../../pages/camera-admin/management/modalVMCameraVoicecallIncoming";
import ModalWakeupCamera from "../../pages/camera-admin/management/modalWakeupCamera";
import useVisionMax from "../../pages/camera-admin/useVisionMax";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";

const VMCameraVoicecalling = () => {
  const { t: tCameras } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  const { getUserACLAccessLevel } = useACL();

  // poll for visionmax incoming call
  const { getLocalStorageItem } = useLocalStorage();
  const enterpriseAccountData = getLocalStorageItem(
    localStorageKeys.ownerOrganization
  );
  const { vmToken, isFetchingVisionMaxGetToken } = useVisionMax();
  const [pausePolling, setPausePolling] = useState(false);
  const {
    data: dataVMDashNotify,
    isFetching: isFetchingVMDashNotify,
    isError: isErrorVMDashNotify,
    error: errorVMDashNotify,
  } = useDashNotifyQuery(undefined, {
    skip:
      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CAMERA_INCOMING_CALL) <
        ROLE_ACCESS_LEVELS.READ ||
      isFetchingVisionMaxGetToken ||
      enterpriseAccountData?.recordertype !== CAMERA_TYPES.VISIONMAX ||
      pausePolling ||
      APP_CONFIG.DEBUG.ALL,
    pollingInterval:
      APP_CONFIG.API_POLLING_INTERVALS.VISIONMAX_VOICECALL_INCOMING,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  // sample response body kept for future reference as it is difficult to test / debug without physical camera devices
  // -----------------------------------------------------------------------------------------------------------------
  // const tempDataVMDashNotify = {
  //   data: [],
  //   result: true,
  //   code: "S00000",
  //   message: "Success",
  //   doc_version: "latest",
  //   uq: "001956c1-f654-4c27-9158-56386532e34e",
  //   build: "1.3.2959",
  //   notify_list: {
  //     voip: {
  //       credential: {
  //         credentials: {
  //           key: "ASIARRJSGX2YYQH273ZG",
  //           secret: "6SlWg01l47D5rYnTz0zYEauw536BmeuT+kcYyuL1",
  //           token:
  //             "FwoGZXIvYXdzEJ///////////wEaDO70PNPAk4tugkWRuiK8BD6H5FVFRVNQsvSg7huQ7FHbKcdh8T4Wr7fk2Hjiux49sSnXM1WHc2mrMPyuwCjzi0YhSm8gMu+9fDnDuKDENknIKZnlJTdU+BuO9pXERNNvzX/Z8vWSp1ARcaNCYpzR8FBN4z5YGyVIr8mtNSTp8IrNIHKg2CwrYdLd1787FKG5AkCye9vAqFgvkHsakd7W4iDG3BYLczQk35DQR0k6V/xWAOSr0ew0AF9dJY9z9lXy0mepqKHUx+u5Of30V2OwIEGWPhyFojviXE+xdPWhqUI3sCDxJByhUI7JNzfk1WSo8fTxHa3acPdSLmdPV2Hd7MhdTtaoZiV8EzBzHLHEp4logyu9/bTNjjY9kKoe2wBUryrt+eZlMoQG5WNthKq2aG2lLBS8qNZCMk/4uuTrntBzsxyXZy33fealsk/7Dc970ogHx/HldQYm6MHX2CP6tNuO7FeDglehCx8VbPjK9lvhSf0tnL0Iz1tnT23mVUGOZrSMHQocWA5IYOVYlrG/SnFyaGYPVAUUmiWdwyZUSHIF+hNCIFI7eh2KGD73zQGrb988aOks7y+j3ZfMFOBMJL7hnyqm+XhKmOQR36iBVnZVwnhUZWFFFHtuZagPWGQhEtop/xsnoZ/vWHm5MjBTuDIsbNFF9lYd1bemV+pt1p2GSJJHZAAtKQaynTSm5akZrbtSRrJI3RPVpjV0NjXNymqOa+iDuT1VeITyOhPHRd1KasqwzPRYHARAQqrJUKwvpJtpj5dCPjjZ4/mBKPTPxcIGMi1fzsxZ065IKHmG75coKU/jPoY9bgPxyXB/pohNDFelafMaCpUZ8Ea7GYI/S4s=",
  //           expires: 1750166392,
  //           topics: {
  //             credential:
  //               "mdt-VisionMax-staging/viewer/voip/0/2255392/credential/b09b2109-a273-4be3-8dab-bd63b112fbda",
  //             device_report:
  //               "mdt-VisionMax-staging/viewer/b09b2109-a273-4be3-8dab-bd63b112fbda",
  //           },
  //           client_id: [
  //             "viewer-b09b2109-a273-4be3-8dab-bd63b112fbda-0_2255392",
  //           ],
  //         },
  //         region: "us-east-1",
  //         deviceGateway: "a1c5bvsmdje7oz-ats.iot.us-east-1.amazonaws.com",
  //         cid: "d87d16f8-29e7-47b1-994a-c06846674126",
  //         prefix: "mdt-VisionMax-staging/Sprint/K220",
  //         request_id: "b09b2109-a273-4be3-8dab-bd63b112fbda",
  //         root: "mdt-VisionMax-staging",
  //         remaining_time: 999999,
  //         unit_name: "J0424150097",
  //         signaling_state: null,
  //       },
  //     },
  //   },
  // };

  useEffect(() => {
    if (!!dataVMDashNotify && !isFetchingVMDashNotify) {
      const notifyData = dataVMDashNotify; // tempDataVMDashNotify can be used to testing
      if (
        !!(notifyData as VMDashNotifyResponseIncomingCall)?.notify_list?.voip &&
        modalsState.showModalVMCameraVoicecallIncoming === null
      ) {
        if (APP_CONFIG.DEBUG.ALL) console.log("pausing polling");
        setPausePolling(true);
        dispatch(
          setModalsData({
            ...modalsState,
            showModalVMCameraVoicecallIncoming:
              notifyData as VMDashNotifyResponseIncomingCall,
          })
        );
      }
    }
  }, [
    dataVMDashNotify,
    isFetchingVMDashNotify,
    pausePolling,
    modalsState.showModalVMCameraVoicecallIncoming,
    // tempDataVMDashNotify, // remove once testing done
  ]);

  return (
    <>
      {/* Wakeup Camera */}
      {!!modalsState.showModalWakeupCamera?.serial_number &&
        !!modalsState.showModalWakeupCamera?.cid && (
          <ModalWakeupCamera
            cameraId={modalsState.showModalWakeupCamera?.cid ?? ""}
            serialNum={modalsState.showModalWakeupCamera?.serial_number ?? ""}
          />
        )}

      {/* Liveview */}
      {!!modalsState.showModalVMCameraLiveviewSN && (
        <ModalVMCameraLiveview
          cameraSerialNumber={modalsState.showModalVMCameraLiveviewSN}
          onHideCallback={() => {
            // refetch camera device listing
            flushSync(() => {
              dispatch({
                type: `${reducerPathCamera}/invalidateTags`,
                payload: [CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING],
              });
              dispatch({
                type: `${reducerPathAdmin}/invalidateTags`,
                payload: [AdminApiTags.VEHICLE_RECORDER_STATUS],
              });
            });
            // dispatch(
            //   setModalsData({
            //     ...modalsState,
            //     showModalVMCameraLiveviewSN: null,
            //   })
            // );
          }}
        />
      )}

      {/* Liveview Continue */}
      {!!modalsState.showModalVMCameraLiveviewContinueSN && (
        <ModalVMCameraLiveviewContinue />
      )}

      {/* Outgoing Voice Call */}
      {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CAMERA_OUTGOING_CALL) >=
        ROLE_ACCESS_LEVELS.READ &&
        !!modalsState.showModalVMCameraVoicecallSN && (
          <ModalVMCameraVoicecall
            cameraSerialNumber={modalsState.showModalVMCameraVoicecallSN}
            onHideCallback={() => {
              // refetch camera device listing
              flushSync(() => {
                dispatch({
                  type: `${reducerPathCamera}/invalidateTags`,
                  payload: [CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING],
                });
                dispatch({
                  type: `${reducerPathAdmin}/invalidateTags`,
                  payload: [AdminApiTags.VEHICLE_RECORDER_STATUS],
                });
              });
              // dispatch(
              //   setModalsData({
              //     ...modalsState,
              //     showModalVMCameraVoicecallSN: null,
              //   })
              // );
            }}
          />
        )}

      {/* Incoming Voice Call */}
      {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CAMERA_INCOMING_CALL) >=
        ROLE_ACCESS_LEVELS.READ &&
        !!modalsState.showModalVMCameraVoicecallIncoming && (
          <ModalVMCameraVoicecallIncoming
            incomingCall={modalsState.showModalVMCameraVoicecallIncoming}
            onHideCallback={() => {
              setPausePolling(false);
            }}
          />
        )}
    </>
  );
};
export default VMCameraVoicecalling;
